/**
 * Admin screen to view details for a ledger (lines filtered by date for a ledger)
 */
import React, { Component } from 'react';
import graphql from 'babel-plugin-relay/macro';
import { QueryRenderer } from 'react-relay';
import { Redirect } from 'react-router-dom';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import AbEnvironment from '../auth/AbEnvironment';
import PropTypes from 'prop-types';
import withStyles from '@mui/styles/withStyles';
import LoadingSpinner from '../ui/LoadingSpinner';
import PageTitle from '../ui/PageTitle';
import ServerError from '../ui/ServerError';
import AppBindStyles from '../../AppBindStyles';
import { Grid, Table, TableBody, TableCell, TableHead, TablePagination, TableRow, TextField } from '@mui/material';
import { addDays, isValid, startOfDay, subMonths } from 'date-fns';

const abEnvironment = new AbEnvironment();
const LedgerQuery = graphql`
query LedgerQuery($ledgerid: UUID, $startDate: Datetime, $endDate: Datetime, $first: Int, $offset: Int) {
  getledgerentriesbyledgeranddate(ledgerid: $ledgerid, startTs: $startDate, endTs: $endDate, first: $first, offset: $offset) {
    totalCount
    nodes {
      accountId
      accountName
      ledgerId
      ledgerName
      ledgerLineId
      ledgerLineDescription
      apId
      transactionId
      transactionDescription
      transactionTime
      changeType
      atId
      debitAmount
      creditAmount
    }
  }
}
`;

const styleSheet = theme => ({
    root: {
      flexGrow: 1,
      //marginTop: 10,
    },
    flex: {
      flex: 1,
    },
});

class Ledger extends Component {
  constructor(props) {
    super(props);

    let cToDate = new Date();
    let tDate = localStorage.getItem('cToDate');
    if (tDate != null) {
      cToDate = new Date(tDate);
    }
    //search todate is "start of next day"
    let searchToDateUtc = startOfDay(addDays(cToDate, 1)).toISOString();

    let cFromDate;
    let fDate = localStorage.getItem('cFromDate');
    if (fDate != null) {
      cFromDate = new Date(fDate);
    } else {
      cFromDate = subMonths(cToDate, 1);
    }
    //search from date is "start of day"
    let searchFromDateUtc = startOfDay(cFromDate).toISOString();

    this.state = {
      startDate: cFromDate,
      queryStartDateUtc: searchFromDateUtc,
      endDate: cToDate,
      queryEndDateUtc: searchToDateUtc,
      page: 0,
      rowsPerPage: 10,
    };  
  }

  goTo(url) {
    if (url == null)
      return;
      
    this.props.history.push(`${url}`);
  }        

  handleChange = name => event => {
    this.setState({
      [name]: event.target.value,
      page: 0,
    });
  };

  setFromDate(theDate) {
    let vdate = new Date(theDate);
    if (isValid(vdate)) {
      localStorage.setItem('cFromDate', vdate);
      let searchDateUtc = startOfDay(vdate).toISOString();
      this.setState({ startDate: vdate, queryStartDateUtc: searchDateUtc, page: 0 });
    }
  }

  setToDate(theDate) {
    let vdate = new Date(theDate);
    if (isValid(vdate)) {
      localStorage.setItem('cToDate', vdate);
      let searchDateUtc = startOfDay(addDays(vdate, 1)).toISOString();
      this.setState({ endDate: vdate, queryEndDateUtc: searchDateUtc, page: 0 });
    }
  }

  handleChangePage = (event, page) => {
    this.setState({page: page});
  }

  handleChangeRowsPerPage = (event) => {
    this.setState({ rowsPerPage: event.target.value, page : 0});
  }

  render() {
    const { isAuthenticatedAppBind, logError } = this.props.auth;
    const classes = this.props.classes;

    if (!isAuthenticatedAppBind()) { 
      return ( <Redirect to='/home' /> );
    }    

    const params = new URLSearchParams(this.props.location.search);
    let ledgerId = params.get('ledgerId');
    let ledgerName = params.get('ledgerName');
    let accountName = params.get('accountName');

    // Determine the offset
    let offset = 0;
    if (this.state.page > 0)
    {
      offset = this.state.page * this.state.rowsPerPage
    }
    let first = this.state.rowsPerPage;

    let totalCount = 0;

    let theTitle = accountName + ' Ledger: ' + ledgerName;

    return (
      <div className={classes.root}>
        <PageTitle title={theTitle}/>
        <Grid container spacing={2}>
          <Grid item style={AppBindStyles.datePickerField}>
            <DatePicker
              autoOk
              label="From date"
              value={this.state.startDate}
              onChange={(date) => this.setFromDate(date) }
              format="YYYY-MM-DD"
              disableFuture
              renderInput={(params) => <TextField {...params} />}
            />
          </Grid>
          <Grid item style={AppBindStyles.datePickerField}>
            <DatePicker
              autoOk
              label="To date"
              value={this.state.endDate}
              onChange={(date) => this.setToDate(date) }
              format="YYYY-MM-DD"
              disableFuture
              renderInput={(params) => <TextField {...params} />}
            />
          </Grid>
        </Grid>
        <QueryRenderer
          environment={abEnvironment.getEnvironment()}
          query={LedgerQuery}
          variables={{startDate: this.state.queryStartDateUtc,
                      endDate: this.state.queryEndDateUtc,
                      ledgerid: ledgerId,
                      first: first,
                      offset: offset}}
          render={({error, props})=> {
            if (error) {
              logError(error.message);
              return (
                <ServerError/>
              );
            } else if (props) {
              // Determine offset information
              totalCount = props.getledgerentriesbyledgeranddate.totalCount;

              /*let headerText = 'No data in this data range';

              if (totalCount && totalCount > 0)
              {
                headerText = 'Account ' + props.getledgerentriesbyledgeranddate.nodes[0].accountName + 
                              ', Ledger ' + props.getledgerentriesbyledgeranddate.nodes[0].ledgerName;
              }*/
              return (
                <div>
                  <div className={classes.paper}>
                    <Table>
                      <TableHead className={classes.tblHead}>
                        <TableRow className={classes.tblHeadRow}>
                          <TableCell className={classes.tblHeadCell}>Date</TableCell>
                          <TableCell className={classes.tblHeadCell}>Line</TableCell>
                          <TableCell className={classes.tblHeadCell}>Transaction</TableCell>
                          <TableCell className={classes.tblHeadCell}>Type</TableCell>
                          <TableCell className={classes.tblHeadCell}>Debit amt</TableCell>
                          <TableCell className={classes.tblHeadCell}>Credit amt</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        { props.getledgerentriesbyledgeranddate.nodes.map(n => {
                           let transactionLink = "transaction?transactionId=" + n.transactionId;
                           return (
                            <TableRow key={n.ledgerLineId}>
                              <TableCell onClick={() => {this.goTo(transactionLink);}}>{n.transactionTime}</TableCell>
                              <TableCell>{n.ledgerLineDescription}</TableCell>
                              <TableCell>{n.transactionDescription}</TableCell> {/* transactionId link */}
                              <TableCell>{n.changeType}</TableCell>
                              <TableCell align="right">{n.debitAmount}</TableCell>
                              <TableCell align="right">{n.creditAmount}</TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>

                  <div style={AppBindStyles.paginationTableFooter}>
                    <TablePagination
                      rowsPerPageOptions={[10, 50, 200]}
                      component="div"
                      count={totalCount}
                      rowsPerPage={this.state.rowsPerPage}
                      page={this.state.page}
                      backIconButtonProps={{
                        'aria-label': 'Previous Page',
                      }}
                      nextIconButtonProps={{
                        'aria-label': 'Next Page',
                      }}
                      onPageChange={this.handleChangePage}
                      onRowsPerPageChange={this.handleChangeRowsPerPage}
                    />                        
                  </div>
                </div>
              )
            }
            return (
              <LoadingSpinner />
            );
          }}
        />
      </div>
    );
  }
}
  
Ledger.propTypes = {
  classes: PropTypes.object.isRequired,
};
  
export default withStyles(styleSheet)(Ledger);
